import React, { useState, useEffect } from 'react';
import { Download, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * The `beforeinstallprompt` event payload (Chromium-only, not in lib.dom).
 * userChoice resolves to an OutcomeMessages-shaped object with `outcome`
 * ('accepted' | 'dismissed') and `platform`.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export default function InstallPwaButton() {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  const [isIOS] = useState(() => {
    if (typeof window === 'undefined') return false;
    const ua = window.navigator.userAgent;
    return (!!ua.match(/iPad/i) || !!ua.match(/iPhone/i)) && !!ua.match(/WebKit/i) && !ua.match(/CriOS/i);
  });

  const [isInstallable, setIsInstallable] = useState(() => {
    if (typeof window === 'undefined') return false;
    const ua = window.navigator.userAgent;
    const isIOSSafari = (!!ua.match(/iPad/i) || !!ua.match(/iPhone/i)) && !!ua.match(/WebKit/i) && !ua.match(/CriOS/i);
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 'standalone' in window.navigator;
    return isIOSSafari && !isStandalone;
  });

  const [showIOSPrompt, setShowIOSPrompt] = useState(false);

  useEffect(() => {

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (isIOS) {
      // Toggle rather than auto-hide: the iOS steps require the user to open
      // the Share sheet and scroll, so a 5-second timer dismissed the help
      // before it could be followed.
      setShowIOSPrompt(prev => !prev);
      return;
    }

    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setIsInstallable(false);
    }
    setDeferredPrompt(null);
  };

  if (!isInstallable) return null;

  return (
    <div className="relative">
      <button
        onClick={handleInstallClick}
        // Compact icon-only on phones so it fits the header's icon row; the
        // full pill with its label returns from sm upwards.
        className="flex items-center gap-2 p-1.5 sm:px-3 sm:py-2 bg-[#859F4A] text-white rounded-lg sm:rounded-xl shadow-md hover:bg-[#6c823b] transition-colors text-[11px] font-bold uppercase tracking-wider active:scale-[0.95]"
        title={t('installPwa.title')}
      >
        <Download size={16} />
        <span className="hidden md:inline">{t('installPwa.install')}</span>
      </button>

      {showIOSPrompt && (
        <div className="absolute top-full mt-2 right-0 w-64 bg-slate-900 text-white text-xs p-4 rounded-xl shadow-xl z-50 animate-in slide-in-from-top-2">
          <button
            type="button"
            onClick={() => setShowIOSPrompt(false)}
            aria-label={t('common.close', 'Schließen')}
            className="absolute top-2 right-2 p-1 text-slate-400 hover:text-white transition-colors"
          >
            <X size={14} />
          </button>
          <p className="mb-2 pr-5"><strong>{t('installPwa.description')}</strong></p>
          <ol className="list-decimal pl-4 space-y-1">
            <li dangerouslySetInnerHTML={{ __html: t('installPwa.install') }} />
            <li dangerouslySetInnerHTML={{ __html: t('installPwa.installed') }} />
          </ol>
          <div className="absolute -top-2 right-6 w-4 h-4 bg-slate-900 rotate-45" />
        </div>
      )}
    </div>
  );
}

